// Central product data — styles ordered per client instruction:
// Insight → Edit → Outlook → Sharp → Scope → Foresee → Zoom → Focus
// Colourways per style follow the client's Model 1–8 sheet (Black first).

export const COMMON_SPEC =
  "Every style is made from recycled co-polyester, fitted with recycled lenses, BPA free, and comes complete with a recycled felt pouch, combining quality, comfort, and a commitment to sustainability.";

export const COLOURWAYS = {
  "Black":         { swatch: "#111111", desc: "A timeless black finish with a premium soft-touch feel. Clean, versatile, and designed to complement every look." },
  "Brown Tort":    { swatch: "#6B3418", desc: "A classic tortoiseshell pattern with rich brown tones and a premium soft-touch finish. Warm, timeless, and effortlessly versatile." },
  "Navy":          { swatch: "#001F5B", desc: "A deep navy finish with a premium soft-touch feel. A refined alternative to black, offering subtle colour with timeless appeal." },
  "Evergreen":     { swatch: "#024932", desc: "A rich forest green with a premium soft-touch finish. Fresh yet understated, adding a natural touch to a classic silhouette." },
  "Red":           { swatch: "#C7332A", desc: "A vibrant red finish with a premium soft-touch feel. Bold without being overpowering, bringing confidence and personality to any frame." },
  "Toffee":        { swatch: "#8A5A2C", desc: "A warm honey-toned tortoiseshell with a premium soft-touch finish. Light, inviting, and perfect for adding warmth to your look." },
  "Cinnamon":      { swatch: "#8B3A1E", desc: "A deep cinnamon tortoiseshell with a premium soft-touch finish. Rich, sophisticated, and inspired by natural earthy tones." },
  "Crystal":       { swatch: "#D6D3CC", desc: "A clear crystal finish with a polished appearance that offers a clean, lightweight look. Fresh, modern, and easy to wear." },
  "Grey Crystal":  { swatch: "#8A8C88", desc: "A translucent grey crystal finish with a polished appearance. Understated and contemporary, offering a softer alternative to solid colours." },
};

const cslug = { "Brown Tort": "tort", "Grey Crystal": "grey" };
const slug  = (s) => s.toLowerCase().replace(/\s+/g, "-");
const csl   = (c) => cslug[c] || slug(c);
const img   = (style, colour, view = "front") => `/assets/glasses/${slug(style)}-${csl(colour)}-${view}.png`;

const buildVariants = (styleName, colours) => colours.map((c) => ({
  color: c,
  slug: csl(c),
  swatch: COLOURWAYS[c].swatch,
  desc: COLOURWAYS[c].desc,
  img: img(styleName, c, "front"),
  gallery: [
    img(styleName, c, "front"),
    img(styleName, c, "side"),
    img(styleName, c, "angle"),
  ],
}));

export const STYLES = [
  {
    slug: "insight", name: "Insight", tagline: "Softly sculpted square.",
    description: "Insight features a softly sculpted square silhouette with clean lines and gentle curves. Finished with a premium soft-touch rubber coating, this versatile frame offers a comfortable, secure fit and a timeless look. Its balanced proportions complement a wide range of face shapes, making it a dependable choice for reading and everyday tasks.",
    size: { frame: "146mm", lens: "50mm", lensH: "43mm", bridge: "15mm", temple: "144mm" },
    variants: buildVariants("Insight", ["Black","Cinnamon","Evergreen","Crystal","Red"]),
  },
  {
    slug: "edit", name: "Edit", tagline: "Contemporary square.",
    description: "Edit is a contemporary square frame defined by clean lines and softened contours. Its balanced silhouette offers a flattering fit across a wide range of face shapes, creating a look that is both modern and timeless.",
    size: { frame: "146mm", lens: "51mm", lensH: "41mm", bridge: "12mm", temple: "151mm" },
    variants: buildVariants("Edit", ["Black","Brown Tort","Navy","Grey Crystal","Toffee"]),
  },
  {
    slug: "outlook", name: "Outlook", tagline: "Modern aviator profile.",
    description: "Outlook is a modern aviator-inspired frame defined by its sleek profile and softly rounded silhouette. Blending timeless styling with contemporary simplicity, it offers a comfortable, flattering fit that transitions effortlessly from work to leisure.",
    size: { frame: "144mm", lens: "54mm", lensH: "47mm", bridge: "11.5mm", temple: "147mm" },
    variants: buildVariants("Outlook", ["Black","Brown Tort"]),
  },
  {
    slug: "sharp", name: "Sharp", tagline: "Confident defined square.",
    description: "Sharp makes a bold impression with its defined square silhouette and clean, confident lines. Strong angles are balanced with subtle curves to create a frame that feels refined rather than overpowering, offering a distinctive look with lasting versatility.",
    size: { frame: "141mm", lens: "47mm", lensH: "40mm", bridge: "15mm", temple: "152mm" },
    variants: buildVariants("Sharp", ["Black","Brown Tort","Evergreen","Red"]),
  },
  {
    slug: "scope", name: "Scope", tagline: "Sleek rectangular.",
    description: "Scope is a sleek rectangular frame with clean lines and a refined silhouette. Its understated design delivers a polished look that feels effortlessly versatile, making it a dependable choice for those who appreciate classic simplicity.",
    size: { frame: "137mm", lens: "44mm", lensH: "35mm", bridge: "12mm", temple: "140mm" },
    variants: buildVariants("Scope", ["Black","Cinnamon","Evergreen","Crystal","Toffee"]),
  },
  {
    slug: "foresee", name: "Foresee", tagline: "Classic rounded square.",
    description: "Foresee is a classic rounded square frame that combines timeless design with modern simplicity. Its softly curved silhouette, keyhole bridge, and refined rivet detailing create a balanced look that is both distinctive and versatile.",
    size: { frame: "143mm", lens: "47mm", lensH: "40mm", bridge: "14mm", temple: "145mm" },
    variants: buildVariants("Foresee", ["Black","Cinnamon","Navy","Toffee"]),
  },
  {
    slug: "zoom", name: "Zoom", tagline: "Soft rectangular.",
    description: "Zoom combines a soft rectangular silhouette with smooth lines and balanced proportions for a refined, understated look. Its clean design offers effortless versatility, making it a timeless choice that complements any wardrobe.",
    size: { frame: "140mm", lens: "47mm", lensH: "35mm", bridge: "14mm", temple: "149mm" },
    variants: buildVariants("Zoom", ["Black","Brown Tort","Navy"]),
  },
  {
    slug: "focus", name: "Focus", tagline: "Modern soft square.",
    description: "Focus is a modern soft square frame that blends clean lines with gently rounded edges for a balanced, versatile look. Its lightweight silhouette complements a wide range of face shapes, offering a timeless style with a relaxed, approachable feel.",
    size: { frame: "140mm", lens: "50mm", lensH: "41mm", bridge: "14mm", temple: "150mm" },
    variants: buildVariants("Focus", ["Grey Crystal","Brown Tort"]),
  },
];

export const findStyle = (s) => STYLES.find((x) => x.slug === s);

// Flatten to individual "product" cards (style × colour)
export const ALL_PRODUCTS = STYLES.flatMap((s) =>
  s.variants.map((v) => ({
    key: `${s.slug}-${v.slug}`,
    styleSlug: s.slug,
    styleName: s.name,
    tagline: s.tagline,
    colourName: v.color,
    colourSlug: v.slug,
    swatch: v.swatch,
    img: v.img,
    gallery: v.gallery,
  }))
);
